import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, getCountFromServer, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { consolidateLeads } from "../utils/leadUtils";
import {
  PERIODS, periodWindows, summarizeLeads, summarizeListings, summarizeViews, topListings,
} from "../utils/overviewData";

const daysFor = (periodId) => (PERIODS.find((period) => period.id === periodId) || PERIODS[0]).days;

function sumReels(totals, docSnap) {
  const reel = docSnap.data();
  return {
    live: totals.live + (reel.status === "active" ? 1 : 0),
    views: totals.views + (Number(reel.views) || 0),
    saves: totals.saves + (Number(reel.saves) || 0),
    enquiries: totals.enquiries + (Number(reel.enquiries) || 0),
  };
}

/**
 * Everything the dealer dashboard shows. Leads and listings are live, as on
 * the Leads and Listings pages; views (one doc per opening, so they grow
 * fast) and reel totals are read once per period and on refresh, and views
 * only from the chart's first day.
 */
export function useDealerOverview(uid, periodId) {
  const days = daysFor(periodId);
  const [now, setNow] = useState(() => Date.now());
  const [rawLeads, setRawLeads] = useState(null);
  const [leadsFailed, setLeadsFailed] = useState(false);
  const [items, setItems] = useState(null);
  const [viewDocs, setViewDocs] = useState(null);
  const [previousViews, setPreviousViews] = useState(null);
  const [reels, setReels] = useState(null);
  const [errors, setErrors] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const runRef = useRef(0);

  const win = useMemo(() => periodWindows(days, now), [days, now]);

  useEffect(() => {
    if (!uid) return undefined;
    setRawLeads(null);
    setLeadsFailed(false);
    return onSnapshot(
      collection(db, "users", uid, "leads"),
      (snap) => setRawLeads(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("Dashboard leads listener failed:", err);
        setLeadsFailed(true);
      }
    );
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    setItems(null);
    return onSnapshot(
      query(collection(db, "properties"), where("ownerId", "==", uid)),
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("Dashboard listings listener failed:", err);
        setItems([]);
        setErrors((prev) => ({ ...prev, listings: true }));
      }
    );
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    const run = ++runRef.current;
    const views = collection(db, "users", uid, "property_views");
    setRefreshing(true);

    const viewsRead = getDocs(query(views, where("viewedAt", ">=", new Date(win.series[0].start))))
      .then((snap) => snap.docs.map((d) => d.data()));
    // A week's chart already holds the week before; a month's does not.
    const previousRead = win.series.length > win.days
      ? Promise.resolve(null)
      : getCountFromServer(query(
        views,
        where("viewedAt", ">=", new Date(win.prevStart)),
        where("viewedAt", "<", new Date(win.prevEnd))
      )).then((snap) => snap.data().count);
    const reelsRead = getDocs(query(collection(db, "propertyReels"), where("dealerId", "==", uid)))
      .then((snap) => snap.docs.reduce(sumReels, { live: 0, views: 0, saves: 0, enquiries: 0 }));

    Promise.allSettled([viewsRead, previousRead, reelsRead]).then(([viewsResult, previousResult, reelsResult]) => {
      if (run !== runRef.current) return;
      const viewsFailed = viewsResult.status === "rejected" || previousResult.status === "rejected";
      if (viewsFailed) {
        console.error("Dashboard views read failed:", viewsResult.reason || previousResult.reason);
        setViewDocs((prev) => prev ?? []);
      } else {
        setViewDocs(viewsResult.value);
        setPreviousViews(previousResult.value);
      }
      if (reelsResult.status === "fulfilled") setReels(reelsResult.value);
      else console.error("Dashboard reels read failed:", reelsResult.reason);
      setErrors((prev) => ({ ...prev, views: viewsFailed, reels: reelsResult.status === "rejected" }));
      setUpdatedAt(Date.now());
      setRefreshing(false);
    });

    return () => {
      runRef.current += 1;
    };
  }, [uid, win]);

  const refresh = useCallback(() => setNow(Date.now()), []);

  const consolidated = useMemo(() => (rawLeads ? consolidateLeads(rawLeads) : null), [rawLeads]);
  const leads = useMemo(() => (consolidated ? summarizeLeads(consolidated, win) : null), [consolidated, win]);
  const views = useMemo(
    () => (viewDocs ? summarizeViews(viewDocs, win, previousViews) : null),
    [viewDocs, previousViews, win]
  );
  const listings = useMemo(() => (items ? { ...summarizeListings(items, win), items } : null), [items, win]);
  const top = useMemo(
    () => (items && consolidated && views ? topListings(items, consolidated, views.byListing, win, 4) : null),
    [items, consolidated, views, win]
  );
  const recentLeads = useMemo(() => (consolidated ? consolidated.slice(0, 5) : null), [consolidated]);

  const status = leadsFailed ? "error" : leads && listings && views ? "ready" : "loading";

  return {
    status,
    refreshing: refreshing && status === "ready",
    updatedAt,
    refresh,
    win,
    leads,
    views,
    listings,
    top,
    reels,
    recentLeads,
    errors,
  };
}
